import { LoadingButton } from '@mui/lab';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { showCompanyEditor } from 'src/store/slices/companies/companiesSlice';
import { ICompanyListItem, CompanyProfileType } from 'src/services/connections/connections.types';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

interface ListCompanyRowActionsProps {
  row: ICompanyListItem;
  onView: (companyId: string) => void;
  onRemoveFromList: (row: ICompanyListItem) => void;
  isRemoving?: boolean;
}

/**
 * Row kebab for a company inside a saved list: View, Edit (unlinked profiles only), and Remove from
 * list. Removing severs this list membership only — the company profile is untouched.
 */
export default function ListCompanyRowActions({ row, onView, onRemoveFromList, isRemoving }: ListCompanyRowActionsProps) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const popover = usePopover();

  const confirm = useBoolean();

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        <MenuItem onClick={() => { popover.onClose(); onView(row.id); }}>
          <Iconify icon="solar:eye-bold" />
          <Typography variant="body2">{t('common.view')}</Typography>
        </MenuItem>

        {row.type === CompanyProfileType.UNLINKED && (
          <MenuItem onClick={() => { popover.onClose(); dispatch(showCompanyEditor(row.id)); }}>
            <Iconify icon="material-symbols:edit" />
            <Typography variant="body2">{t('common.edit')}</Typography>
          </MenuItem>
        )}

        <MenuItem sx={{ color: 'error.main' }} onClick={() => { popover.onClose(); confirm.onTrue(); }}>
          <Iconify icon="ix:remove-circle-filled" />
          <Typography variant="body2">{t('lists.actions.remove_from_list')}</Typography>
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('lists.actions.remove_from_list')}
        content={t('lists.actions.remove_from_list_confirm', { count: 1 })}
        action={
          <LoadingButton
            loading={isRemoving}
            variant="contained"
            color="error"
            onClick={() => { onRemoveFromList(row); confirm.onFalse(); }}
          >
            {t('common.remove')}
          </LoadingButton>
        }
      />
    </>
  );
}
